import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useQualifications } from '../../hooks';
import { Button, Input, Card, CardHeader, CardBody, Alert, Spinner, FieldOfWorkField, EditResetWarning, EditResetModal, needsEditReset, EDIT_RESET_ACK_STATE } from '../../components';
import type { QualificationType } from '../../types';

const qualificationTypes = [
  { value: 'certification', label: 'Certification' },
  { value: 'license', label: 'License' },
  { value: 'degree', label: 'Degree' },
  { value: 'diploma', label: 'Diploma' },
  { value: 'training', label: 'Training' },
  { value: 'other', label: 'Other' },
];

export const EditQualification: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { qualifications, loading, error, updateQualification, clearError } = useQualifications();
  const [success, setSuccess] = useState(false);
  const [allowEdit, setAllowEdit] = useState(
    Boolean((location.state as { editResetAcknowledged?: boolean } | null)?.editResetAcknowledged),
  );

  const [formData, setFormData] = useState({
    title: '',
    institution: '',
    description: '',
    qualification_type: 'certification' as QualificationType,
    field_of_work: '',
    issue_date: '',
    expiry_date: '',
    credential_id: '',
    credential_url: '',
  });
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!loading && qualifications.length > 0 && id) {
      const qual = qualifications.find(q => q.id === id);
      if (qual) {
        setFormData({
          title: qual.title || '',
          institution: qual.institution || '',
          description: (qual as { description?: string }).description || '',
          qualification_type: (qual.qualification_type as QualificationType) || 'certification',
          field_of_work: qual.field_of_work || '',
          issue_date: qual.issue_date || '',
          expiry_date: qual.expiry_date || '',
          credential_id: qual.credential_id || '',
          credential_url: qual.credential_url || '',
        });
        setReady(true);
      }
    }
  }, [qualifications, loading, id]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    setSuccess(false);

    const data = { ...formData };
    if (data.credential_url && !/^https?:\/\//i.test(data.credential_url)) {
      data.credential_url = `https://${data.credential_url}`;
    }

    const result = await updateQualification(id, data);
    if (result.success) {
      setSuccess(true);
      setTimeout(() => navigate('/qualifications'), 1500);
    }
  };

  if (loading && !ready) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!loading && !ready) {
    return (
      <div>
        <Alert variant="danger">Qualification not found.</Alert>
      </div>
    );
  }

  const currentStatus = qualifications.find(q => q.id === id)?.verification_status;
  const showResetModal = needsEditReset(currentStatus) && !allowEdit;

  return (
    <div>
      <Card collapsible={false}>
        <CardHeader>
          <h1 className="text-xl md:text-2xl font-bold">Edit Qualification</h1>
        </CardHeader>
        <CardBody>
          {error && (
            <Alert variant="danger" onClose={clearError} className="mb-4">
              {error}
            </Alert>
          )}
          {success && (
            <Alert variant="success" className="mb-4">
              Qualification updated successfully!
            </Alert>
          )}
          <EditResetWarning status={currentStatus} />

          <form onSubmit={handleSubmit} className="space-y-6">
            <Input
              label="Title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
            />

            <Input
              label="Institution"
              name="institution"
              value={formData.institution}
              onChange={handleChange}
              required
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Qualification Type</label>
              <select
                name="qualification_type"
                value={formData.qualification_type}
                onChange={handleChange}
                className="w-full px-4 py-3.5 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 min-h-[48px] text-base md:px-3 md:py-2 md:min-h-0"
                required
              >
                {qualificationTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>

            <FieldOfWorkField
              value={formData.field_of_work}
              onChange={field_of_work => setFormData(prev => ({ ...prev, field_of_work }))}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                className="w-full px-4 py-3.5 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 min-h-[100px] text-base md:px-3 md:py-2 md:min-h-0"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input label="Issue Date" name="issue_date" type="date" value={formData.issue_date} onChange={handleChange} />
              <Input label="Expiry Date" name="expiry_date" type="date" value={formData.expiry_date} onChange={handleChange} />
            </div>

            <Input
              label="Credential ID"
              name="credential_id"
              value={formData.credential_id}
              onChange={handleChange}
              placeholder="e.g., CERT-12345"
            />

            <Input
              label="Credential URL"
              name="credential_url"
              type="text"
              placeholder="e.g. facebook.com or https://example.com"
              value={formData.credential_url}
              onChange={handleChange}
            />

            <div className="flex flex-col sm:flex-row gap-3">
              <Button type="submit" loading={loading} fullWidth className="sm:w-auto">
                Save Changes
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate('/qualifications')}
                fullWidth
                className="sm:w-auto"
              >
                Cancel
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
      {showResetModal && (
        <EditResetModal
          status={currentStatus}
          onCancel={() => navigate('/qualifications')}
          onContinue={() => {
            setAllowEdit(true);
            navigate('.', { replace: true, state: EDIT_RESET_ACK_STATE });
          }}
        />
      )}
    </div>
  );
};
